import React, { forwardRef, useEffect, useState } from 'react';
import { getOrderById, createReceipt } from '../../services/orderApi.js';
import Receipt from './Receipt.js';
import Token from './Token.js';
import useAppStore from '../../store/useAppStore.js';

/**
 * OrderPrintContent - Print receipt/token for orders
 * Uses the Receipt and Token components to display order details
 * printMode: 'receipt' | 'token' | 'both' | 'none'
 */
const OrderPrintContent = forwardRef(({ order, onReady, printMode = 'receipt' }, ref) => {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const { selectedBranch } = useAppStore((state) => ({
    selectedBranch: state.selectedBranch
  }));

  useEffect(() => {
    setOrderDetails(null);
    setLoading(true);
    if (order?.id) {
      loadOrderDetails();
    }
  }, [order?.id]);

  const loadOrderDetails = async () => {
    try {
      const response = await getOrderById(order.id);
      let orderDetails = response.order;

      // Create receipt if it doesn't exist
      if (!orderDetails.receipt) {
        try {
          const receiptResponse = await createReceipt(order.id);
          orderDetails.receipt = receiptResponse.receipt;
        } catch (receiptError) {
          console.error('Error creating receipt:', receiptError);
          // Continue without receipt for now
        }
      }

      setOrderDetails(orderDetails);
      setLoading(false);
      if (typeof onReady === 'function') {
        onReady();
      }
    } catch (error) {
      console.error('Error loading order details:', error);
      setLoading(false);
      if (typeof onReady === 'function') {
        onReady();
      }
    }
  };

  if (loading) {
    return <div ref={ref}>Loading...</div>;
  }

  if (!orderDetails) {
    return <div ref={ref}>Order not found</div>;
  }

  // Transform order items to match Receipt format
  const receiptItems = (orderDetails.items || []).map((item) => ({
    id: item.id,
    name: item.product_name || item.variant_name || 'Product',
    price: item.unit_price,
    quantity: item.quantity,
  }));

  const transformedOrder = {
    id: orderDetails.id,
    order_type: orderDetails.order_type,
    createdAt: new Date(orderDetails.created_at).toLocaleString(),
    items: receiptItems,
    discount: orderDetails.discount || 0,
    total: orderDetails.total,
    table: {
      name: `ORD-${orderDetails.id}`,
      label: `Order #${orderDetails.id}`
    },
    receipt: orderDetails.receipt,
    token: orderDetails.token,
    order_number: orderDetails.order_number,
    waiter_name: orderDetails.waiter_name
  };

  const renderContent = () => {
    switch (printMode) {
      case 'receipt':
        return (
          <Receipt
            order={transformedOrder}
            receiptItems={receiptItems}
            discount={orderDetails.discount || 0}
            selectedTable={`ORD-${orderDetails.id}`}
            tables={[]}
            branch={selectedBranch}
          />
        );
      case 'token':
        return (
          <Token
            order={transformedOrder}
            branch={selectedBranch}
          />
        );
      case 'both':
        return (
          <div>
            <Receipt
              order={transformedOrder}
              receiptItems={receiptItems}
              discount={orderDetails.discount || 0}
              selectedTable={`ORD-${orderDetails.id}`}
              tables={[]}
              branch={selectedBranch}
            />
            <div className="page-break" style={{ pageBreakAfter: 'always', marginTop: '10mm' }} />
            <Token
              order={transformedOrder}
              branch={selectedBranch}
            />
          </div>
        );
      case 'none':
      default:
        return <div>No print content</div>;
    }
  };

  return (
    <div ref={ref}>
      {renderContent()}
    </div>
  );
});

OrderPrintContent.displayName = 'OrderPrintContent';

export default OrderPrintContent;
